import React, { useState, useEffect } from 'react';
import { Award, Lock, Sparkles, Share2, Twitter, Facebook, Linkedin, Link2, Check } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const BadgeDisplay = () => {
  const [allBadges, setAllBadges] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    fetchBadges();
    checkForNewBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const [allResponse, userResponse] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/user/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAllBadges(allResponse.data);
      setEarnedBadges(userResponse.data.earned_badges);
    } catch (err) {
      console.error('Failed to fetch badges:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewBadges = async () => {
    try {
      const response = await axios.post(`${API}/user/check-badges`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.newly_earned && response.data.newly_earned.length > 0) {
        setNewBadges(response.data.newly_earned);
        response.data.newly_earned.forEach(badge => {
          toast({
            title: `🏆 Badge Earned!`,
            description: `${badge.name}: ${badge.description}`,
          });
        });
        // Refresh badges
        fetchBadges();
      }
    } catch (err) {
      console.error('Failed to check badges:', err);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'from-gray-500 to-gray-600';
      case 'rare': return 'from-blue-500 to-blue-600';
      case 'epic': return 'from-purple-500 to-purple-600';
      case 'legendary': return 'from-yellow-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getRarityBorder = (rarity) => {
    switch (rarity) {
      case 'common': return 'border-gray-500/30';
      case 'rare': return 'border-blue-500/30';
      case 'epic': return 'border-purple-500/30';
      case 'legendary': return 'border-yellow-500/30 animate-pulse';
      default: return 'border-gray-500/30';
    }
  };

  const earnedBadgeIds = new Set(earnedBadges.map(b => b.id));

  // Get the public achievements URL
  const getAchievementsUrl = () => {
    return `${window.location.origin}/achievements/${userId}`;
  };

  // Share to Twitter/X
  const shareToTwitter = (badge) => {
    const text = `🏆 I just earned the "${badge.name}" badge on Seven Chapter Books! ${badge.description}`;
    const url = getAchievementsUrl();
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank',
      'width=550,height=420'
    );
    setShareMenuOpen(null);
  };

  // Share to Facebook
  const shareToFacebook = (badge) => {
    const url = getAchievementsUrl();
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`I earned the "${badge.name}" badge!`)}`,
      '_blank',
      'width=550,height=420'
    );
    setShareMenuOpen(null);
  };

  // Share to LinkedIn
  const shareToLinkedIn = (badge) => {
    const url = getAchievementsUrl();
    const text = `I just earned the "${badge.name}" badge on Seven Chapter Books! ${badge.description}`;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank',
      'width=550,height=420'
    );
    setShareMenuOpen(null);
  };

  // Copy link to clipboard
  const copyLink = async () => {
    const url = getAchievementsUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast({
        title: 'Link Copied!',
        description: 'Share your achievements page with friends',
      });
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the link manually',
        variant: 'destructive',
      });
    }
    setShareMenuOpen(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Award className="h-5 w-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-white">Badges & Achievements</h3>
        </div>
        <span className="text-gray-400 text-sm">
          {earnedBadges.length} / {allBadges.length} earned
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-red-500 to-yellow-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${(earnedBadges.length / allBadges.length) * 100}%` }}
        />
      </div>

      {/* New Badges Animation */}
      {newBadges.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-4 border border-yellow-500/30">
          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="h-5 w-5 text-yellow-400 animate-pulse" />
            <span className="text-yellow-400 font-semibold">New Badges Earned!</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {newBadges.map(badge => (
              <span key={badge.id} className="text-2xl animate-bounce">{badge.icon}</span>
            ))}
          </div>
        </div>
      )}

      {/* Badges Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {allBadges.map((badge) => {
          const isEarned = earnedBadgeIds.has(badge.id);
          const earnedBadge = earnedBadges.find(b => b.id === badge.id);
          const isShareMenuOpen = shareMenuOpen === badge.id;
          
          return (
            <div
              key={badge.id}
              className={`relative group p-4 rounded-xl border transition-all duration-300 ${
                isEarned 
                  ? `bg-gradient-to-br ${getRarityColor(badge.rarity)} bg-opacity-20 ${getRarityBorder(badge.rarity)} hover:scale-105` 
                  : 'bg-gray-800/50 border-gray-700 opacity-50 hover:opacity-70'
              }`}
              data-testid={`badge-${badge.id}`}
            >
              {/* Share Button (only for earned badges) */}
              {isEarned && (
                <div className="absolute top-2 right-2 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareMenuOpen(isShareMenuOpen ? null : badge.id);
                    }}
                    className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`share-badge-${badge.id}`}
                  >
                    <Share2 className="h-4 w-4 text-white" />
                  </button>
                  
                  {/* Share Dropdown Menu */}
                  {isShareMenuOpen && (
                    <div className="absolute right-0 mt-1 w-40 bg-gray-900 border border-white/10 rounded-lg shadow-xl overflow-hidden z-20">
                      <button
                        onClick={() => shareToTwitter(badge)}
                        className="w-full px-3 py-2 flex items-center space-x-2 hover:bg-white/10 text-white text-sm"
                      >
                        <Twitter className="h-4 w-4 text-blue-400" />
                        <span>Twitter/X</span>
                      </button>
                      <button
                        onClick={() => shareToFacebook(badge)}
                        className="w-full px-3 py-2 flex items-center space-x-2 hover:bg-white/10 text-white text-sm"
                      >
                        <Facebook className="h-4 w-4 text-blue-500" />
                        <span>Facebook</span>
                      </button>
                      <button
                        onClick={() => shareToLinkedIn(badge)}
                        className="w-full px-3 py-2 flex items-center space-x-2 hover:bg-white/10 text-white text-sm"
                      >
                        <Linkedin className="h-4 w-4 text-blue-600" />
                        <span>LinkedIn</span>
                      </button>
                      <button
                        onClick={copyLink}
                        className="w-full px-3 py-2 flex items-center space-x-2 hover:bg-white/10 text-white text-sm border-t border-white/10"
                      >
                        {copiedLink ? (
                          <>
                            <Check className="h-4 w-4 text-green-400" />
                            <span className="text-green-400">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 text-gray-400" />
                            <span>Copy Link</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Badge Icon */}
              <div className="text-center">
                <span className={`text-4xl ${isEarned ? '' : 'grayscale'}`}>
                  {badge.icon}
                </span>
                
                {/* Lock overlay for unearned */}
                {!isEarned && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                    <Lock className="h-6 w-6 text-gray-500" />
                  </div>
                )}
              </div>
              
              {/* Badge Info */}
              <div className="mt-3 text-center">
                <h4 className={`text-sm font-semibold ${isEarned ? 'text-white' : 'text-gray-500'}`}>
                  {badge.name}
                </h4>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                  {badge.description}
                </p>
                
                {/* Rarity Tag */}
                <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full capitalize ${
                  isEarned 
                    ? `bg-gradient-to-r ${getRarityColor(badge.rarity)} text-white` 
                    : 'bg-gray-700 text-gray-500'
                }`}>
                  {badge.rarity}
                </span>
                
                {/* Earned Date */}
                {isEarned && earnedBadge?.earned_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    Earned {new Date(earnedBadge.earned_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BadgeDisplay;
